import { Controller } from '@hotwired/stimulus';

class AuthenticatorCodeController extends Controller {
  static targets = ['input', 'hiddenInput', 'submit', 'resendLink', 'errorMessage'];
  static values = {
    length: Number,
    lastAttemptAt: String,
    errorCode: String,
    cooldownSeconds: { type: Number, default: 30 }
  };

  connect() {
    this.clearInputs();
    this.updateSubmitState();
    this.startResendCooldown();
    this.inputTargets[0].focus();
    if (this.errorCodeValue === 'invalid_code') {
      this.showErrorState();
    }
  }

  clearInputs() {
    this.inputTargets.forEach((input) => (input.value = ''));
    this.hiddenInputTarget.value = '';
  }

  handlePaste(event) {
    const pasteData = (event.clipboardData || window.clipboardData)
      .getData('text')
      .trim();
    const digits = pasteData
      .replace(/\D/g, '')
      .slice(0, this.lengthValue)
      .split('');

    this.clearInputs();

    digits.forEach((digit, index) => {
      if (this.inputTargets[index]) {
        this.inputTargets[index].value = digit;
      }
    });

    const nextIndex =
      digits.length < this.lengthValue ? digits.length : this.lengthValue - 1;
    this.inputTargets[nextIndex].focus();
    this.updateHiddenInput();
  }

  handleInput(event) {
    this.clearErrorState();

    const input = event.target;
    const index = parseInt(event.params.index, 10);

    input.value = input.value.replace(/\D/g, '');

    if (input.value && index < this.lengthValue - 1) {
      this.inputTargets[index + 1].focus();
    }

    this.updateHiddenInput();
  }

  handleKeydown(event) {
    const input = event.target;
    const index = parseInt(event.params.index, 10);

    if (event.key === 'Backspace' && !input.value && index > 0) {
      this.inputTargets[index - 1].focus();
    }
  }

  updateHiddenInput() {
    const fullCode = this.inputTargets.map((i) => i.value).join('');
    this.hiddenInputTarget.value = fullCode;

    this.updateSubmitState();
  }

  updateSubmitState() {
    const isComplete = this.inputTargets.every((i) => i.value !== '');

    this.submitTarget.disabled = !isComplete;
  }

  startResendCooldown() {
    if (!this.hasResendLinkTarget) return;
    const lastAttemptAt = this.lastAttemptAtValue;
    if (!lastAttemptAt) {
      this.enableResendLink();
      return;
    }

    const lastAttemptTime = new Date(lastAttemptAt).getTime();
    const currentTime = new Date().getTime();
    const timeSinceLastAttempt = currentTime - lastAttemptTime;
    const remainingCooldown = this.cooldownSecondsValue * 1000 - timeSinceLastAttempt;

    if (remainingCooldown <= 0) {
      this.enableResendLink();
    } else {
      this.disableResendLink(remainingCooldown);
    }
  }

  showErrorState() {
    this.inputTargets.forEach((input) => {
      input.classList.add('is-invalid');
    });

    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.remove('d-none');
    }
  }

  disableResendLink(remainingTime) {
    if (!this.hasResendLinkTarget) return;

    this.resendLinkTarget.disabled = true;
    this.resendLinkTarget.classList.add('disabled');

    let timeLeft = Math.ceil(remainingTime / 1000);

    this.resendCountdownInterval = setInterval(() => {
      if (timeLeft > 0) {
        this.resendLinkTarget.textContent = `Renvoyer le code (${timeLeft}s)`;
        timeLeft--;
      } else {
        clearInterval(this.resendCountdownInterval);
        this.enableResendLink();
      }
    }, 1000);
  }

  enableResendLink() {
    if (!this.hasResendLinkTarget) return;

    this.resendLinkTarget.disabled = false;
    this.resendLinkTarget.classList.remove('disabled');
    this.resendLinkTarget.textContent = 'Renvoyer le code';
  }

  clearErrorState() {
    this.inputTargets.forEach((input) => {
      input.classList.remove('is-invalid');
    });

    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add('d-none');
    }
  }
}

export default AuthenticatorCodeController;
